import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
    DataTable, columnTypes, LoadingSpinner, number, baseEvent, hitType, Table, dateTranslate, LEVELS, PlayerName,
    mapPlaylistFromVideoSearch, BaseRunners, Dropdown, styles, pitchTypeDisplayName, CheckboxDropdown,
} from 'yankees-react-utils';
import { Advance, StrikeZones, useOktaQuery } from '../../../data';
import useConfig from '../../../utils/useConfig';
import ReportCard from '../../../components/ReportCard';
import StatTooltip from '../../../components/StatTooltip';
import StrikeZone from '../../../components/StrikeZone';
import VideoPlaylistButton from '../../../components/VideoPlaylistButton';
import VideoPlayerButton from '../../../components/VideoPlayerButton';
import useOpposingTeamSelector from '../../../utils/useOpposingTeamSelector';
import usePlayerSelector from '../../../utils/usePlayerSelector';
import PlayerAuthoredReportNote from '../../../components/Notes/PlayerAuthoredReportNote';
import OpposingBatterMatchupNote from './OpposingBatterMatchupNote';
import OpposingPitcherKeyTakeawaysNote from './OpposingPitcherKeyTakeawaysNote';
import { store } from '../../../store';

const { TEXT, PERCENT } = columnTypes;

const StyledFilterPill = styled.button`
    font-size: 12px;
    background-color: ${(props) => (props.$isDisabled ? '#eeeeee' : 'white')};
    color: ${(props) => (props.$isDisabled ? '#8f8f8f' : '#303030')}
`;

const StyledCheckboxDropdown = styled(CheckboxDropdown)`
    .dropdown-toggle {
        width: 90px;
    }
`;

const InputField = ({ label, children }) => (
    <div className="d-flex align-items-center me-4">
        <span className="me-1">{label}</span>
        {children}
    </div>
);

const PastMatchupsReport = (
    { isOpposingPitcherVersion, pitcher, pitcherId, batter, batterId, currentYear, isLHB, isLHP },
) => {
    const { state: { user } } = useContext(store);
    const { t: translate } = useTranslation();
    const { CurrentYear } = useConfig();
    const defaultStartYear = { name: (CurrentYear - 3).toString(), value: CurrentYear - 3 };
    const defaultEndYear = { name: CurrentYear.toString(), value: CurrentYear };
    const pitcherIsStarter = pitcher?.PrimaryPosition === 11;
    const opposingTeam = useOpposingTeamSelector();
    const { IsPitcher } = usePlayerSelector();
    const allLevel = { abbr: translate('common.all'), name: translate('common.all'), id: 0, SortOrder: 0, checked: true };
    const [innerLevels, setInnerLevels] = useState([allLevel, ...LEVELS]);
    const [selectedLevelIds, setSelectedLevelIds] = useState([]);
    const [matchupatbatsUse, setMatchupatbatsUse] = useState([]);
    const [pitchbreakdownUse, setPitchbreakdownUse] = useState([]);

    const [yearStart, setYearStart] = useState(defaultStartYear);
    const [yearEnd, setYearEnd] = useState(defaultEndYear);
    const yearOptions = [CurrentYear, CurrentYear - 1, CurrentYear - 2, CurrentYear - 3, CurrentYear - 4, CurrentYear - 5, CurrentYear - 6, CurrentYear - 7, CurrentYear - 8, CurrentYear - 9, CurrentYear - 10].map((y) => ({ name: y.toString(), value: y }));
    const validYearCombo = yearStart && yearEnd && yearEnd.value >= yearStart.value;
    const [totalPitches, setTotalPitches] = useState({ tot: 0, firstpitch: 0, twostrike: 0 });
    const [pitchTypes, setPitchTypes] = useState([]);

    const onLevelDropdownChange = (o, key, e) => {
        if (o.id === 0 && e.target.checked) {
            const ilm = innerLevels.map((l, i) => ({
                ...l,
                checked: !i,
            }));
            setInnerLevels(ilm);
            setSelectedLevelIds(ilm.filter((x) => x.checked).map((obj) => obj.id));
        } else {
            const newLevels = innerLevels.map((lo) => ({
                ...lo,
                checked: lo.name === o.name ? e.target.checked : !!lo.checked,
            }));
            if (newLevels[0].checked && newLevels.filter((l) => !!l.checked).length > 1) {
                newLevels[0].checked = false;
            } else if (!newLevels.filter((l) => !!l.checked).length) {
                newLevels[0].checked = true;
            }

            setInnerLevels(newLevels);
            setSelectedLevelIds(newLevels.filter((x) => x.checked).map((obj) => obj.id));
        }
    };

    const sumTotalPitchesProp = (array, prop) => array.reduce((acc, obj) => acc + obj[prop], 0);
    const setDefaultTotalPitches = (ptb) => {
        const allMpt = ptb?.matchuppitchtypes?.matchuptotal;
        setTotalPitches(
            {
                tot: sumTotalPitchesProp(allMpt, 'pit'),
                firstpitch: sumTotalPitchesProp(allMpt, 'totalpit00count'),
                twostrike: sumTotalPitchesProp(allMpt, 'totalpit2strikes'),
            },
        );
    };

    const { data: pitchbreakdown, isLoading: isBreakdownLoading } = useOktaQuery(['breakdown', pitcherId, batterId, yearStart, yearEnd],
        (t) => Advance.pastmatchup(t, { batterId, pitcherId, startyearid: yearStart.value, endyearid: yearEnd.value, applymatchupgroupings: true }),
        {
            enabled: validYearCombo,
            onSuccess: (data) => {
                setDefaultTotalPitches(data);
                setPitchbreakdownUse(data);
            },
        });

    const { data: matchupatbats, isLoading: isAtBatsLoading } = useOktaQuery(['atbats', pitcherId, batterId, yearStart, yearEnd],
        (t) => Advance.matchupatbats(t, { batterId, pitcherId, startyearid: yearStart.value, endyearid: yearEnd.value }),
        {
            enabled: validYearCombo,
            onSuccess: (data) => {
                const uniqueLevels = LEVELS.filter((x) => [...new Set(data.atbats.map((item) => item.levelid))].includes(x.id)).map((x) => { x.checked = false; x.name = x.abbr; return x; });
                setInnerLevels([allLevel, ...uniqueLevels]);
                setMatchupatbatsUse(data);
            },
        });

    const { data: contours, isLoading: isContoursLoading } = useOktaQuery(['contours', pitcherId, batterId, opposingTeam.LevelIDData],
        (t) => StrikeZones.getStrikeZoneContours(t, { islhb: isLHB, currentYear, dataLevelId: opposingTeam.LevelIDData }));

    const recalcPageData = () => {
        if (selectedLevelIds.includes(0) || selectedLevelIds.length === 0) { // These are the cases where we're just doing "all"
            setDefaultTotalPitches(pitchbreakdown);
            setMatchupatbatsUse(matchupatbats);
            setPitchbreakdownUse(pitchbreakdown);
            return;
        }

        // For matchupatbats we need to evaluate "atbats" and "pitches"
        if (matchupatbats) {
            const newMatchupAtBats = JSON.parse(JSON.stringify(matchupatbats));
            if (matchupatbats && matchupatbats.atbats) {
                newMatchupAtBats.atbats = newMatchupAtBats.atbats.filter((x) => selectedLevelIds.includes(x.levelid));
            }
            if (matchupatbats && matchupatbats.pitches) {
                newMatchupAtBats.pitches = newMatchupAtBats.pitches.filter((x) => selectedLevelIds.includes(x.levelid));
            }
            setMatchupatbatsUse(newMatchupAtBats);
        }

        // For pitchbreakdown  we need to evaluate 'matchuppitchtypes'>>'matchuppitchtypes' and 'matchuppitchtypes'>>'matchuptotal'
        if (pitchbreakdown) {
            const newPitchBreakdown = JSON.parse(JSON.stringify(pitchbreakdown));
            if (pitchbreakdown && pitchbreakdown.matchuppitchtypes) {
                // We know we're not on "All" / 0 because we would have returned before here
                const targetPitchTypes = pitchbreakdown?.matchuppitchtypes?.matchuptotal.filter((x) => selectedLevelIds.includes(x.levelid));
                setTotalPitches(
                    {
                        tot: sumTotalPitchesProp(targetPitchTypes, 'pit'),
                        firstpitch: sumTotalPitchesProp(targetPitchTypes, 'totalpit00count'),
                        twostrike: sumTotalPitchesProp(targetPitchTypes, 'totalpit2strikes'),
                    },
                );

                newPitchBreakdown.matchuppitchtypes.matchuppitchtypes = newPitchBreakdown.matchuppitchtypes.matchuppitchtypes.filter((x) => selectedLevelIds.includes(x.levelid));
                newPitchBreakdown.matchuppitchtypes.matchuptotal = newPitchBreakdown.matchuppitchtypes.matchuptotal.filter((x) => selectedLevelIds.includes(x.levelid));
            }
            setPitchbreakdownUse(newPitchBreakdown);
        }
    };

    useEffect(() => {
        if (!matchupatbatsUse || !matchupatbatsUse.pitches) return;
        const pitchtypesraw = (matchupatbatsUse.pitches.filter((pitch) => pitch.fxtype).map((pitch) => ({
            fxtype: pitch.fxtype,
            fxsubtype: pitch.fxsubtype,
            type: pitch.type,
        })));

        const arrayUniqueByKey = [...new Map(pitchtypesraw.map((item) => [`${item.fxtype}-${item.fxsubtype}`, item])).values()];
        setPitchTypes(arrayUniqueByKey.sort((a, b) => (a.fxtype === 9 || a.fxtype === 10 ? 1 : a.fxtype) - (b.fxtype === 9 || b.fxtype === 10 ? 1 : b.fxtype)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchupatbatsUse]);

    useEffect(() => {
        if (selectedLevelIds != null && selectedLevelIds.length > 0 && pitchbreakdown && matchupatbats) {
            recalcPageData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLevelIds]);

    useEffect(() => {
        setYearStart(defaultStartYear);
        setYearEnd(defaultEndYear);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pitcherId, batterId]);

    function doMakePASeries(pitches, includePitchSeq) {
        const series = [
            { data: [], name: translate('baseball.pitches.fastball-non'), index: 1, id: 'nonfb', color: 'transparent', marker: { symbol: 'circle', radius: 2.5 } },
            { data: [], name: translate('baseball.pitches.fastball_one'), index: 2, id: 'fb', color: 'transparent', marker: { symbol: 'circle', radius: 2.5 } },
            { data: [], name: `${translate('baseball.pitches.fastball-non')} - ${translate('sections.opponent.player.action-pitch')}`, index: 3, id: 'nonfbaction', color: 'transparent', marker: { symbol: 'circle', radius: 2.5 } },
            { data: [], name: `${translate('baseball.pitches.fastball_one')} - ${translate('sections.opponent.player.action-pitch')}`, index: 4, id: 'fbaction', color: 'transparent', marker: { symbol: 'circle', radius: 2.5 } },
        ];
        for (let i = 0; i < pitches.length; i++) {
            if (!pitches[i].ispitch) { break; }

            const point = makePoint(pitches[i], includePitchSeq);

            // eslint-disable-next-line no-nested-ternary
            const sortindex = pitches[i].isfb === 1 ? (pitches[i].islastpitch ? 3 : 1) : (pitches[i].islastpitch ? 2 : 0);
            series[sortindex].data.push(point);
        }
        return series;
    }

    const [matchupChartSeries, setMatchupChartSeries] = useState([
        { name: translate('sections.opponent.player.hard-hit'), visible: false, button: `<svg width="12" height="12" style="margin: 0; padding: 0;" class="me-2"> <path fill="transparent" stroke="#0000ff" d="M 6 1 L 11 6 6 11 1 6 6 1 Z" visibility="inherit" stroke-width="2" /> </svg> ${translate('sections.opponent.player.hard-hit')}` },
        { name: translate('sections.opponent.player.hard-batted-out'), visible: false, button: `<svg class="me-2" width="12" height="12" style="margin:0;padding:0;"><path fill="#0000ff" stroke="#0000ff" d="M 6 1 L 11 11 1 11 Z" visibility="inherit" stroke-width="2"></path></svg> ${translate('sections.opponent.player.hard-batted-out')}` },
        { name: translate('baseball.balls_one'), visible: false, button: `<svg class="me-2" width="12" height="12" style="margin:0;padding:0;"><circle cx="6" cy="6" r="5" stroke="#00ff00" fill="#00ff00" stroke-width="2"></circle></svg> ${translate('baseball.balls_one')}` },
        { name: translate('sections.opponent.player.called-strike'), visible: false, button: `<svg class="me-2" width="12" height="12" style="margin:0;padding:0;"><circle cx="6" cy="6" r="5" stroke="#ff0000" fill="#ff0000" stroke-width="2"></circle></svg> ${translate('sections.opponent.player.called-strike')}` },
        { name: translate('sections.opponent.player.swinging-strike'), visible: false, button: `<svg class="me-2" width="12" height="12" style="margin:0;padding:0;"><path fill="#ff0000" stroke="#ff0000" d="M 6 0 L 12 6 6 12 0 6 6 0 Z" visibility="inherit" stroke-width="0"></path></svg> ${translate('sections.opponent.player.swinging-strike')}` },
        { name: translate('sections.opponent.player.foul'), visible: false, button: `<svg class="me-2" width="12" height="12" style="margin:0;padding:0;"><path fill="transparent" stroke="#ff0000" d="M 1 1 L 11 1 11 11 1 11 Z" visibility="inherit" stroke-width="2"></path></svg> ${translate('sections.opponent.player.foul')}` },
        { name: translate('sections.opponent.player.weak-hit'), visible: false, button: `<svg width="12" height="12" style="margin: 0; padding: 0;" class="me-2"> <path fill="transparent" stroke="#ffa500" d="M 6 1 L 11 6 6 11 1 6 6 1 Z" visibility="inherit" stroke-width="2" /> </svg> ${translate('sections.opponent.player.weak-hit')}` },
        { name: translate('sections.opponent.player.weak-batted-out'), visible: false, button: `<svg class="me-2" width="12" height="12" style="margin:0;padding:0;"><path fill="#ffa500" stroke="#ffa500" d="M 6 1 L 11 11 1 11 Z" visibility="inherit" stroke-width="2"></path></svg> ${translate('sections.opponent.player.weak-batted-out')}` },
    ]);

    const [matchupPitchTypes, setMatchupPitchTypes] = useState([
        { name: translate('sections.opponent.player.first-pitch'), visible: false, startingPitcherOnly: false },
        { name: translate('sections.opponent.player.hitter-ahead'), visible: false, startingPitcherOnly: false },
        { name: `2 ${translate('baseball.strikes_other')}`, visible: false, startingPitcherOnly: false },
        { name: translate('sections.opponent.player.last-pitch'), visible: false, startingPitcherOnly: false },
        { name: `1st ${translate('sections.opponent.player.time-through-order')}`, visible: false, startingPitcherOnly: true },
        { name: `2nd ${translate('sections.opponent.player.time-through-order')}`, visible: false, startingPitcherOnly: true },
        { name: `3rd ${translate('sections.opponent.player.time-through-order')}`, visible: false, startingPitcherOnly: true },
    ]);

    function filterMatchupPitchType(pitch) {
        // if (matchupPitchTypes.filter((x) => x.visible).length === 0) { return true; }
        if (matchupPitchTypes[0].visible && (pitch.balls !== 0 || pitch.strikes !== 0)) { return false; }
        if (matchupPitchTypes[1].visible && pitch.balls <= pitch.strikes) { return false; }
        if (matchupPitchTypes[2].visible && pitch.strikes !== 2) { return false; }
        if (matchupPitchTypes[3].visible && !pitch.islastpitch) { return false; }
        if (matchupPitchTypes[4].visible && pitch.timethroughlineup !== 1) { return false; } // 1 TTO
        if (matchupPitchTypes[5].visible && pitch.timethroughlineup !== 2) { return false; }
        if (matchupPitchTypes[6].visible && pitch.timethroughlineup !== 3) { return false; }
        return true;
    }

    function makePoint(pitch, includePitchSeq) {
        return {
            x: pitch.platex,
            y: pitch.platez,
            name: '',
            tooltip: `${(pitch.velocity ? `${translate('format.abbr-mph', { value: number(pitch.velocity, 1) })}<br/>` : '')
                + (pitch.istop ? `${translate('baseball.abbr-top')} ` : `${translate('baseball.abbr-bottom')} `) + pitch.inning}, ${pitch.outsbefore} ${translate('baseball.outs_one')}, ${pitch.balls}-${pitch.strikes
            }<br />${translate('baseball.batter')}: ${pitch.batter
            }<br />${translate('baseball.pitcher_one')}: ${pitch.pitcher
            }${pitch.islastpitch && (pitch.resulttype === 14 || pitch.resulttype === 15) ? `<br />${hitType(pitch.hittype, false, translate)} ${baseEvent(pitch.baseevent, undefined, undefined, translate)}${pitch.exitvelo ? `, ${translate('sections.opponent.player.exit-velo')} ${number(pitch.exitvelo, 1)}` : ''}` : ''}<br />
            ${translate('sections.opponent.player.time-through-order')}:${pitch.timethroughlineup}`,
            url: pitch.url,
            gameid: pitch.gameid,
            eventseq: pitch.eventseq,
            pitchseq: pitch.pitchseq,
            abpitchseq: pitch.abpitchseq,
            pitch,
            dataLabels: includePitchSeq ? {
                enabled: true,
                inside: true,
                style: {
                    color: styles.theme.fontColors.mineShaft,
                    fontFamily: styles.theme.font.body,
                },
                allowOverlap: true,
                align: 'center',
                verticalAlign: 'middle',
                crop: false,
                formatter: () => pitch.abpitchseq,
            } : {
                enabled: false,
            },
            marker: {
            },
        };
    }

    function matchupSeriesify(pitches) {
        const isAnySeriesEnabled = (matchupChartSeries.filter((x) => x.visible).length > 0);
        const series = [
            { data: [], name: translate('sections.opponent.player.hard-hit'), visible: isAnySeriesEnabled ? matchupChartSeries[0].visible : true, index: 1, id: 'hit', marker: { symbol: 'diamond', radius: 4, lineColor: '#0000ff', fillColor: '#ffffff', lineWidth: 2 } },
            { data: [], name: translate('sections.opponent.player.hard-batted-out'), visible: isAnySeriesEnabled ? matchupChartSeries[1].visible : true, index: 2, id: 'out', marker: { symbol: 'triangle', radius: 5, lineColor: '#0000ff', fillColor: '#0000ff', lineWidth: 2 } },
            { data: [], name: translate('baseball.balls_one'), visible: isAnySeriesEnabled ? matchupChartSeries[2].visible : true, index: 3, id: 'ball', marker: { symbol: 'circle', radius: 5, lineColor: '#00ff00', fillColor: '#00ff00', lineWidth: 2 } },
            { data: [], name: translate('sections.opponent.player.called-strike'), visible: isAnySeriesEnabled ? matchupChartSeries[3].visible : true, index: 4, id: 'stk', marker: { symbol: 'circle', radius: 5, lineColor: '#ff0000', fillColor: '#ff0000', lineWidth: 2 } },
            { data: [], name: translate('sections.opponent.player.swinging-strike'), visible: isAnySeriesEnabled ? matchupChartSeries[4].visible : true, index: 5, id: 'swing', marker: { symbol: 'diamond', radius: 4, lineColor: '#ff0000', fillColor: '#ff0000', lineWidth: 2 } },
            { data: [], name: translate('sections.opponent.player.foul'), visible: isAnySeriesEnabled ? matchupChartSeries[5].visible : true, index: 6, id: 'foul', marker: { symbol: 'square', radius: 4, lineColor: '#ff0000', fillColor: '#ffffff', lineWidth: 2 } },
            { data: [], name: translate('sections.opponent.player.weak-hit'), visible: isAnySeriesEnabled ? matchupChartSeries[6].visible : true, index: 7, id: 'wkhit', marker: { symbol: 'diamond', radius: 4, lineColor: '#ffa500', fillColor: '#ffffff', lineWidth: 2 } },
            { data: [], name: translate('sections.opponent.player.weak-batted-out'), visible: isAnySeriesEnabled ? matchupChartSeries[7].visible : true, index: 8, id: 'wkout', marker: { symbol: 'triangle', radius: 5, lineColor: '#ffa500', fillColor: '#ffa500', lineWidth: 2 } },
        ];

        for (let i = 0; i < pitches.length; i++) {
            if (!pitches[i].ispitch) { break; }

            const result = pitches[i].resulttype;
            const event = pitches[i].baseevent;
            const point = makePoint(pitches[i]);

            if ((result === 14 || result === 15) && event > 0 && event < 5 && pitches[i].wellhit === true) {
                series[0].data.push(point);
            } else if ((result === 14 || result === 15) && pitches[i].wellhit === true && (event === 8 || (event > 10 && event < 16))) {
                series[1].data.push(point);
            } else if (result === 1) {
                series[2].data.push(point);
            } else if (result === 4) {
                series[3].data.push(point);
            } else if (result === 6) {
                series[4].data.push(point);
            } else if (result === 8) {
                series[5].data.push(point);
            } else if ((result === 14 || result === 15) && event > 0 && event < 5 && pitches[i].wellhit === false) {
                series[6].data.push(point);
            } else if ((result === 14 || result === 15) && pitches[i].wellhit === false && (event === 8 || (event > 10 && event < 16))) {
                series[7].data.push(point);
            }
        }
        return series;
    }

    function GetPitchSeriesData(pitchtype) {
        // eslint-disable-next-line no-nested-ternary
        // const heatmapcode = (pitchtype.type === 1 ? 'fb' : (pitchtype.type === 4 ? 'ch' : 'bb'));
        // let targetHeatmap = null;
        // if (heatmaps) {
        //     targetHeatmap = heatmaps.filter((x) => x.Split === 'All' && x.IsLHP === isLHP && x.SubType === heatmapcode); // this should give us one match
        // }

        // if (targetHeatmap == null || !targetHeatmap[0]) {
        //     return null;
        // }

        return {
            pitchtype: pitchtype.fxtype,
            fxsubtype: pitchtype.fxsubtype,
            // bgheatmap: `data:${targetHeatmap[0].ContentType};base64,${targetHeatmap[0].HeatMapImg}`,
            series: matchupSeriesify(matchupatbatsUse.pitches
                .filter((pitch) => pitch.fxtype === pitchtype.fxtype && pitch.fxsubtype === pitchtype.fxsubtype && filterMatchupPitchType(pitch))),
        };
    }

    function toggleVis(isPitchType, name, newVis) {
        if (isPitchType) {
            matchupPitchTypes.filter((x) => x.name === name)[0].visible = newVis;
            setMatchupPitchTypes([...matchupPitchTypes]);
        } else {
            matchupChartSeries.filter((x) => x.name === name)[0].visible = newVis;
            setMatchupChartSeries([...matchupChartSeries]);
        }
    }

    const pitchTypeResultsColumns = [
        {
            title: translate('sections.opponent.player.pitchtype'),
            id: 'pitchtype',
            type: TEXT,
            transform: (row) => `${pitchTypeDisplayName(row, true, true, translate)
                || translate('sections.opponent.player.overall')}${row.pitchsubtype === 94 ? ' > 94' : ''}`,
        },
        {
            id: 'levelid',
            title: translate('baseball.level_one'),
            transform: ({ levelid }) => (LEVELS.find(({ id }) => id === levelid)?.abbr),
            type: TEXT,
        },
        {
            id: 'pit',
            title: translate('sections.opponent.player.abbr-pitch'),
            type: TEXT,
        },
        {
            id: 'percent',
            title: () => (<StatTooltip statistic="PitchTypePercent" placement="right">{translate('sections.opponent.player.usage')} %</StatTooltip>),
            type: PERCENT,
            transform: (row) => (row.pit / totalPitches.tot),
        },
        {
            id: 'firstpercent',
            title: () => (<StatTooltip statistic="PitchTypePercentFirstPitch" placement="right">{translate('sections.opponent.player.abbr-first-pitch')} %</StatTooltip>),
            type: PERCENT,
            transform: (row) => (row.pit00count / totalPitches.firstpitch),
        },
        {
            id: 'twokpercent',
            title: () => (<StatTooltip statistic="PitchTypePercent2K" placement="right">2K %</StatTooltip>),
            type: PERCENT,
            transform: (row) => (row.pit2strikes / totalPitches.twostrike),
        },
        {
            id: 'kzonpercent',
            title: () => (<StatTooltip statistic="KZon" placement="right">{translate('baseball.abbr-strikezone')} %</StatTooltip>),
            type: PERCENT,
            transform: (row) => (row.inzone / row.pit),
        },
        {
            id: 'swpercent',
            title: () => (<StatTooltip statistic="Sw" placement="right">{translate('sections.opponent.player.abbr-swing-pct')}</StatTooltip>),
            type: PERCENT,
            transform: (row) => (row.sw / row.pit),
        },
        {
            id: 'chpercent',
            title: () => (<StatTooltip statistic="Ch" placement="right">{translate('sections.opponent.player.abbr-chase-pct')}</StatTooltip>),
            type: PERCENT,
            transform: (row) => (row.chase / row.outzone),
        },
        {
            id: 'swzonepercent',
            title: () => (<StatTooltip statistic="SwInZon" placement="left">{translate('sections.opponent.player.abbr-swing-zone-pct')}</StatTooltip>),
            type: PERCENT,
            transform: (row) => (row.swinzone / row.inzone),
        },
        {
            id: 'stpercent',
            title: () => (<StatTooltip statistic="Stk" placement="left">{translate('sections.opponent.player.abbr-strike-pct')}</StatTooltip>),
            type: PERCENT,
            transform: (row) => (row.strikes / row.pit),
        },
        {
            id: 'mspercent',
            title: () => (<StatTooltip statistic="Ms" placement="left">{translate('sections.opponent.player.abbr-miss-pct')}</StatTooltip>),
            type: PERCENT,
            transform: (row) => ((row.sw - row.cn) / row.sw),
        },
    ];

    const isLoading = isBreakdownLoading || !pitcher || !batter || isAtBatsLoading || isContoursLoading;// || isHeatmapsLoading;
    if (isLoading) {
        return <LoadingSpinner border="animation" />;
    }

    const allPlaylist = (!validYearCombo || !matchupatbatsUse?.atbats || !matchupatbatsUse?.pitches) ? [] : mapPlaylistFromVideoSearch({
        videos: matchupatbatsUse
            .atbats.reduce((all, ab) => [...all, ...matchupatbatsUse.pitches.filter((p) => p.gameid === ab.gameid && p.eventseq === ab.eventseq)
                .sort((a, b) => a.abpitchseq - b.abpitchseq)], []),
        playervideos: [],
    }, user.Roles.map((r) => r.RoleName));

    const selectedLevels = innerLevels.filter((l) => !!l.checked);
    const levelPlaceholder = selectedLevels.length === 1 ? selectedLevels[0].abbr : `${selectedLevels.length} ${translate('baseball.level_other')}`;

    return (
        <>
            <Row>
                <Col md={8} sm={12}>
                    <div className="mb-3 d-flex align-items-center">
                        <h2 className="mb-0">{translate('sections.opponent.past-matchups')}: <PlayerName name={pitcher.FullName} throws={pitcher.Throws} /> {translate('baseball.abbr-versus')} <PlayerName player={batter} bats={batter.Bats} /> </h2>
                        {matchupatbatsUse?.pitches && (
                            <div className="ms-2">
                                <VideoPlaylistButton
                                    icon={translate('common.all')}
                                    playlist={allPlaylist}
                                    size="3em"
                                />
                            </div>
                        )}
                    </div>
                </Col>
                <Col md={4} sm={12} className="d-flex justify-content-end align-items-center">
                    <InputField label={`${translate('baseball.level_one')}:`}>
                        <StyledCheckboxDropdown
                            value={innerLevels || []}
                            onChange={onLevelDropdownChange}
                            options={innerLevels || []}
                            titleOverrideFormat
                            title={levelPlaceholder}
                            name="leveldropdown"
                        />
                    </InputField>
                    {translate('common.start')}: <Dropdown className="ms-2" options={yearOptions} value={yearStart} onSelect={setYearStart} />
                    <span style={{ marginLeft: '10px' }}>{translate('common.end')}: </span><Dropdown className="ms-2" options={yearOptions} value={yearEnd} onSelect={setYearEnd} align="end" />
                    {!validYearCombo && <span style={{ color: 'red', marginLeft: '10px' }}>{translate('sections.opponent.player.invalid-combo')}</span>}
                </Col>
            </Row>
            {validYearCombo && (
                <>
                    <Row>
                        <Col md={6} sm={12}>
                            {isOpposingPitcherVersion && <PlayerAuthoredReportNote type="mynt" section="1" player={batter} pitcher={pitcher} />}
                            {!isOpposingPitcherVersion && <PlayerAuthoredReportNote type="mynt" section="1" player={pitcher} batter={batter} />}
                        </Col>
                        <Col md={6} sm={12}>
                            {isOpposingPitcherVersion && <OpposingPitcherKeyTakeawaysNote pitcherId={pitcherId} islhb={isLHB} />}
                            {!isOpposingPitcherVersion && <OpposingBatterMatchupNote pitcherId={pitcherId} batterId={batterId} />}
                        </Col>
                    </Row>
                    {!isLoading
                        && (
                            <ReportCard title={translate('sections.opponent.player.pitch-type-breakdown')}>
                                {pitchbreakdownUse && pitchbreakdownUse.matchuppitchtypes?.matchuppitchtypes.length > 0 && totalPitches.tot != null
                                    && (
                                        <DataTable
                                            columns={pitchTypeResultsColumns}
                                            rows={pitchbreakdownUse.matchuppitchtypes.matchuppitchtypes.sort((a, b) => ((a.pitch_order > b.pitch_order) ? 1 : -1))}
                                        />
                                    )}
                                {totalPitches.tot === 0 && <>{translate('sections.opponent.player.not-enough-data')}</>}
                            </ReportCard>
                        )}
                    <hr />
                    {isOpposingPitcherVersion && !isLoading && matchupatbatsUse && matchupatbatsUse.atbats && matchupatbatsUse.atbats.length > 0
                        && (
                            <>
                                <ReportCard title={translate('sections.opponent.player.by-pitch-type')}>
                                    <div className="pull-left">
                                        <div className="d-grid" style={{ gridTemplateColumns: `repeat(${matchupPitchTypes.length}, minmax(0, 1fr))`, gap: '0.5rem' }}>
                                            {matchupPitchTypes && matchupPitchTypes.map((x) => (!x.startingPitcherOnly || (pitcherIsStarter && x.startingPitcherOnly) ? (
                                                <StyledFilterPill
                                                    key={x.name}
                                                    className="py-1 px-2 border rounded d-flex align-items-center fw-bold"
                                                    uib-btn-checkbox="true"
                                                    onClick={() => toggleVis(true, x.name, !x.visible)}
                                                    $isDisabled={!x.visible && !!matchupPitchTypes.some((s) => s.visible)}
                                                >
                                                    {x.name}
                                                </StyledFilterPill>
                                            ) : <span key={x.name}>&nbsp;</span>))}
                                            {/* This is temporarily disabled until we can work out the grid alignment of the image */}
                                            {/* { isOpposingPitcherVersion && (
                                            <span style={{ marginLeft: '10px' }}>
                                                <Checkbox
                                                    id="hitterheat"
                                                    label="Overlay Hitter Heatmaps?"
                                                    checked={overlayHitterHeatmap}
                                                    onChange={(e) => setOverlayHitterHeatmap(e.currentTarget.checked)}
                                                />
                                            </span>
                                        )} */}
                                        </div>
                                        <div className="d-grid mt-2" style={{ gridTemplateColumns: `repeat(${matchupChartSeries.length}, minmax(0, 1fr))`, gap: '0.5rem' }}>
                                            {matchupChartSeries && matchupChartSeries.map((x) => (
                                                // eslint-disable-next-line react/no-danger
                                                <StyledFilterPill
                                                    key={x.name}
                                                    className="py-1 px-2 border rounded d-flex align-items-center fw-bold"
                                                    uib-btn-checkbox="true"
                                                    onClick={() => toggleVis(false, x.name, !x.visible)}
                                                    dangerouslySetInnerHTML={{ __html: x.button }}
                                                    $isDisabled={!x.visible && !!matchupChartSeries.some((s) => s.visible)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    {/* {/* <hr /> */}
                                    <div className="clear container-fluid" style={{ paddingTop: '1em' }}>
                                        <div className="row pitchreviewrow" style={{ paddingBottom: '1em' }}>
                                            {pitchTypes && contours && pitchTypes.map((x) => {
                                                const pitchseriesdata = GetPitchSeriesData(x);

                                                return pitchseriesdata && (
                                                    <div className="col-xs-12 col-sm-6 col-md-19perc text-center" style={{ marginBottom: '10px' }} key={`${x.fxtype}-${x.fxsubtype}`}>
                                                        <h4 key={`${x.fxtype}-${x.fxsubtype}_h4`}>
                                                            {pitchTypeDisplayName(x, false, false, translate) || translate('sections.opponent.player.overall')}
                                                        </h4>
                                                        <StrikeZone
                                                            key={`${x.fxtype}-${x.fxsubtype}_sz`}
                                                            seriesData={pitchseriesdata.series}
                                                            size="sm"
                                                            showlegend={false}
                                                            legendposition="below"
                                                            showrhb={!isLHB}
                                                            showlhb={isLHB}
                                                            yopposite={isLHB}
                                                            showoffgrid
                                                            contourPoints={isLHP ? contours.lhp : contours.rhp}
                                                            hideGrid
                                                            pitcherView={IsPitcher}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </ReportCard>
                                <hr />
                            </>
                        )}
                    {!isLoading && matchupatbatsUse && matchupatbatsUse.atbats && matchupatbatsUse.atbats.length > 0
                        ? (
                            <>
                                <div className="clear container-fluid" style={{ paddingTop: '1em' }}>
                                    <div className="row pitchreviewrow" style={{ paddingBottom: '1em' }}>
                                        {matchupatbatsUse && matchupatbatsUse.pitches && matchupatbatsUse.atbats && contours && matchupatbatsUse.atbats.map((x) => {
                                            const pitchesAB = matchupatbatsUse.pitches.filter((y) => x.gameid === y.gameid && x.eventseq === y.eventseq);
                                            const seriesAB = doMakePASeries(pitchesAB, true);
                                            const playlistPA = mapPlaylistFromVideoSearch({ videos: pitchesAB, playervideos: [] }, user.Roles.map((r) => r.RoleName));
                                            return (
                                                <div className="col-xs-12 col-sm-6 col-md-19perc" style={{ marginBottom: '10px' }} key={`${x.gameid}_${x.eventseq}`}>
                                                    <Table key={`${x.gameid}_${x.eventseq}_table`} striped={false} hover={false}>
                                                        <thead>
                                                            <tr>
                                                                <th className="text-center">
                                                                    {dateTranslate(translate, x.gamedate)} ({LEVELS.find(({ id }) => id === x.levelid)?.abbr})&nbsp;&nbsp;&nbsp;
                                                                    <VideoPlaylistButton icon={translate('baseball.abbr-atbat')} playlist={playlistPA} />
                                                                </th>
                                                            </tr>
                                                            <tr>
                                                                <th className="text-center">
                                                                    {x.atbatseq} {translate('baseball.abbr-atbat')} - {x.result}
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                <td>
                                                                    <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                                                                        <StrikeZone
                                                                            key={`${x.gameid}_${x.eventseq}_sz`}
                                                                            seriesData={seriesAB}
                                                                            size="sm"
                                                                            showlegend={false}
                                                                            legendposition="below"
                                                                            showrhb={!isLHB}
                                                                            showlhb={isLHB}
                                                                            yopposite={isLHB}
                                                                            showoffgrid
                                                                            contourPoints={isLHP ? contours.lhp : contours.rhp}
                                                                            showHeatMapBackground={false}
                                                                            pitcherView={IsPitcher}
                                                                        />
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="text-center">
                                                                    {`${x.battingteamabbr}: ${x.scorebattingbefore} ${x.fieldingteamabbr}: ${x.scorefielding} (${translate('baseball.abbr-inning')} ${x.inning}) `}
                                                                    <BaseRunners baseState={x.runnerson.replace(/_/g, '').split()} height={20} width={30} />
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td>
                                                                    <Table>
                                                                        <thead>
                                                                            <tr>
                                                                                <th>{translate('sections.opponent.player.video')}</th>
                                                                                <th>#</th>
                                                                                <th>{translate('baseball.abbr-count')}</th>
                                                                                <th>{translate('sections.opponent.player.type')}</th>
                                                                                <th>{translate('sections.opponent.player.abbr-velocity')}</th>
                                                                                <th>{translate('common.result')}</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {pitchesAB && pitchesAB.map((z) => (
                                                                                <tr key={z.abpitchseq}>
                                                                                    <td><VideoPlayerButton gameid={z.gameid} eventseq={z.eventseq} pitchseq={z.pitchseq} /></td>
                                                                                    <td>{z.abpitchseq}</td>
                                                                                    <td>{z.balls}-{z.strikes}</td>
                                                                                    <td>{pitchTypeDisplayName(z, true, true, translate)}</td>
                                                                                    <td>{number(z.velocity, 1)}</td>
                                                                                    <td>
                                                                                        {!z.islastpitch && z.pitchresult}
                                                                                        {z.islastpitch && <span style={{ fontWeight: 'bold' }}>{x.batvspitresult}</span>}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </Table>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </>
                        )
                        : <>{translate('sections.opponent.player.not-enough-data')}</>}
                </>
            )}
        </>
    );
};

PastMatchupsReport.propTypes = {
    isOpposingPitcherVersion: PropTypes.bool.isRequired,
    pitcher: PropTypes.shape({
        PlayerID: PropTypes.number.isRequired,
        FullName: PropTypes.shape.isRequired,
        Bats: PropTypes.number.isRequired,
        Throws: PropTypes.number.isRequired,
        Team: PropTypes.shape({
            LevelID: PropTypes.number,
        }),
        LevelID: PropTypes.number,
        PrimaryPosition: PropTypes.number.isRequired,
    }),
    batter: PropTypes.shape({
        PlayerID: PropTypes.number.isRequired,
        FullName: PropTypes.shape.isRequired,
        Bats: PropTypes.number.isRequired,
        Throws: PropTypes.number.isRequired,
    }),
    batterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    pitcherId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    currentYear: PropTypes.number.isRequired,
    isLHP: PropTypes.bool.isRequired,
    isLHB: PropTypes.bool.isRequired,
};
PastMatchupsReport.defaultProps = {
    pitcher: null,
    batter: null,
};

export default PastMatchupsReport;
